// Query-string sanitizer shared by the UI and API traces.
//
// The traces are attached to feedback reports that humans read, and search
// text is personal. So this is an allowlist: a key keeps its value only when
// it names an id, a code, an enum or paging. Every other key keeps its name
// and gets `[redacted]`, which still tells the reader "a filter was active".

const MAX_KEYS = 10
const MAX_VALUE_LEN = 80
const REDACTED = '[redacted]'

const SAFE_KEYS = new Set(['sort', 'order', 'page', 'limit', 'offset', 'status', 'type'])
const SAFE_SUFFIXES = ['_uid', '_id', '_code', '_ids', '_uids', '_codes']

function isSafeKey(key: string): boolean {
  return SAFE_KEYS.has(key) || SAFE_SUFFIXES.some((suffix) => key.endsWith(suffix))
}

/** Returns the query string (without `?`) with non-allowlisted values
 * redacted, or `undefined` when there is no query. Never throws. */
export function sanitizeQuery(search: string): string | undefined {
  try {
    const params = new URLSearchParams(search)
    const parts: string[] = []
    for (const [key, value] of params) {
      if (parts.length >= MAX_KEYS) break
      const shown = isSafeKey(key) ? value.slice(0, MAX_VALUE_LEN) : REDACTED
      parts.push(`${encodeURIComponent(key)}=${shown === REDACTED ? shown : encodeURIComponent(shown)}`)
    }
    return parts.length > 0 ? parts.join('&') : undefined
  } catch {
    return undefined
  }
}
